
import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface CelebrationOverlayProps {
  show: boolean;
  message?: string;
  onDone: () => void;
}

interface Particle {
  id: number;
  x: number;
  y: number;
  color: string;
  size: number;
  rotation: number;
  delay: number;
  duration: number;
  type: 'confetti' | 'star' | 'circle';
}

const COLORS = [
  '#0d9488', '#14b8a6', '#10b981', '#f59e0b',
  '#f97316', '#ec4899', '#8b5cf6', '#ef4444',
  '#06b6d4', '#84cc16',
];

function generateParticles(count: number): Particle[] {
  const particles: Particle[] = [];
  for (let i = 0; i < count; i++) {
    const types: Particle['type'][] = ['confetti', 'star', 'circle'];
    particles.push({
      id: i,
      x: Math.random() * 100,
      y: -10 - Math.random() * 20,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      size: 6 + Math.random() * 10,
      rotation: Math.random() * 360,
      delay: Math.random() * 0.6,
      duration: 1.5 + Math.random() * 1.5,
      type: types[Math.floor(Math.random() * types.length)],
    });
  }
  return particles;
}

export default function CelebrationOverlay({ show, message, onDone }: CelebrationOverlayProps) {
  const [visible, setVisible] = useState(false);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [fadeOut, setFadeOut] = useState(false);

  const startCelebration = useCallback(() => {
    setParticles(generateParticles(50));
    setVisible(true);
    setFadeOut(false);

    const fadeTimer = setTimeout(() => {
      setFadeOut(true);
    }, 2200);

    const hideTimer = setTimeout(() => {
      setVisible(false);
      setParticles([]);
      onDone();
    }, 3000);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [onDone]);

  useEffect(() => {
    if (show) {
      const cleanup = startCelebration();
      return cleanup;
    }
  }, [show, startCelebration]);

  if (!visible) return null;

  return createPortal(
    <div
      className={`fixed inset-0 pointer-events-none flex items-center justify-center transition-opacity duration-700 ${
        fadeOut ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ zIndex: 9999999 }}
    >
      {/* Confetti particles */}
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.type === 'confetti' ? p.size * 0.4 : p.size,
            backgroundColor: p.color,
            borderRadius: p.type === 'circle' ? '50%' : p.type === 'star' ? '2px' : '2px',
            transform: `rotate(${p.rotation}deg)`,
            animation: `celebrationFall ${p.duration}s ease-in ${p.delay}s forwards`,
            opacity: 0,
          }}
        />
      ))}

      {/* Center message */}
      <div
        className="relative z-10 px-8 py-5 rounded-2xl bg-white/95 backdrop-blur-sm shadow-2xl border border-teal-100"
        style={{
          animation: 'celebrationPop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
        }}
      >
        <div className="text-center">
          <div className="text-3xl mb-2" style={{ animation: 'celebrationBounce 0.6s ease 0.3s both' }}>
            🎉
          </div>
          <p className="text-sm font-bold text-gray-800 mb-1">
            {message || '目標達成おめでとう！'}
          </p>
          <p className="text-xs text-teal-600 font-medium">素晴らしい！この調子で頑張りましょう！</p>
        </div>
      </div>

      <style>{`
        @keyframes celebrationFall {
          0% {
            opacity: 1;
            transform: translateY(0) rotate(0deg) scale(1);
          }
          100% {
            opacity: 0;
            transform: translateY(100vh) rotate(720deg) scale(0.3);
          }
        }
        @keyframes celebrationPop {
          0% {
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            opacity: 1;
            transform: scale(1.1);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes celebrationBounce {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.4);
          }
          100% {
            transform: scale(1);
          }
        }
      `}</style>
    </div>,
    document.body
  );
}
